import Input from 'components/forms/Input'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import ThresholdSelect from 'components/forms/specific/ThresholdSelect'
import ConfirmModal from 'components/modal/ConfirmModal'
import { groupBy, isNil, keyBy, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Other from '../../../../../components/actions/Other'
import DtoFile from '../../../../../components/file/dto/DtoFile'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import {
    getLocalizationLabel,
    getLocalizationPicto,
    getQualificationIcon,
} from '../../../../../utils/AnalyseUtils'
import { getDate, getFullDate } from '../../../../../utils/DateUtil'
import { hasValue } from '../../../../../utils/NumberUtil'
import { statusIcon } from '../../../../../utils/StatusUtil'
import {
    getLabel,
    getLabelTruncate,
    getObjectLabel,
} from '../../../../../utils/StoreUtils'
import { searchAllCharacters } from '../../../../../utils/StringUtil'
import OperationAction from '../../actions/OperationAction'
import DtoDetailOperation from '../../dto/DtoDetailOperation'
import DtoRemark from '../../dto/DtoRemark'
import AnalysisPopin from './AnalysisPopin'
import DtoPrettyAnalysisLight from '../../../dashboard/dto/DtoPrettyAnalysisLight'
import ToastrAction from '../../../../../toastr/actions/ToastrAction'
import { getQualifications, getStatuses, UNQUALIFIED } from '../../../../../utils/QualityUtils'
import { SuperParameterGraphModal } from '../../../qualityComponents/ParameterGraph'
import AnalysisStepper from '../stepper/AnalysisStepper'
import { Grid2, Stack } from '@mui/material'
import DtoSampling from '../../dto/DtoSampling'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import ExportFileModal from 'components/modal/ExportFileModal'
import { exportModelFile, formatData, getModelFileType } from 'utils/ExportDataUtil'
import ExportAction from 'export/actions/ExportAction'
import { ALIGN, NewTable, WrapperBox } from 'components/datatable/NewTable'
import { calculateThresholdsResult, getMarkerColor } from 'utils/QualityThresholdUtils'
import useActions from 'utils/customHook/useActions'
import { EMPTY_SELECTION, THRESHOLD } from 'quality/constants/QualityConstants'

const tableHeaders = ['nullValue', 'place', 'parameter', 'result', 'unit', 'remarkCode', 'detectionLimit', 'quantificationLimit', 'support', 'fraction', 'laboratory', 'sampling', 'analysisDate']
const exportHeaders = ['parameterCode', 'parameter', 'result', 'value', 'unit', 'unitLabel', 'remarkCode', 'remark', 'detectionLimit', 'quantificationLimit', 'supportCode', 'support', 'fractionCode', 'fraction', 'laboratory', 'sampling', 'analysisDate', 'sampleDate', 'statusCode', 'status', 'qualificationCode', 'qualification', 'place']

const OperationAnalysisPanel = ({
    id,
    code,
    operation,
    analysis: propsAnalysis,
    remarks,
    threshold,
    onChangeThreshold,
    uploadFile = () => {},
    files,
    getOperationFiles,
    sampleId,
    defaultThreshold,
    samplings,
}) => {
    const dispatch = useDispatch()

    const {
        supports,
        contributorsIndex,
        parametersIndex,
        unitsIndex,
        qualitometer,
        sandreCodes,
        typeEnvironmentModels,
        qualityThresholds,
        fractions,
    } = useSelector(store => ({
        supports: store.SupportReducer.supports,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
        qualitometer: store.QualityReducer.qualitometer,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        fractions: store.FractionReducer.fractions,
    }), shallowEqual)

    const [selections, setSelections] = useState([])
    const [selection, setSelection] = useState(EMPTY_SELECTION)
    const [searchValue, setSearchValue] = useState('')
    const [isOpenChartPopin, setIsOpenChartPopin] = useState(false)
    const [isOpenAnalysisPopin, setIsOpenAnalysisPopin] = useState(false)
    const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false)
    const [isOpenAnalysisStepper, setIsOpenAnalysisStepper] = useState(false)
    const [selectedParam, setSelectedParam] = useState()
    const [analysis, setAnalysis] = useState({})
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [index, setIndex] = useState()

    useActions(() => {
        const actions = {
            importFile: {
                onClick: uploadFile,
                format: '',
                tooltip: i18n.importAnalysisFile,
            },
            new: () => setIsOpenAnalysisStepper(true),
            calculate: [operation.id],
            exportList: [{
                onClick: () => setIsExportModalOpen(true),
                label: i18n.excel,
            }],
            controlAnalysis: { stations: [qualitometer.id], operations: [operation.id] },
        }
        if (files.length) {
            actions.other = {
                other: (
                    <Other
                        className='clickable'
                        tooltip={i18n.operation}
                        icon='attach_file'
                        onClick={getOperationFiles}
                    />
                ),
            }
        }
        return actions
    }, [operation, qualitometer ])

    const reloadOperations = () => {
        dispatch(OperationAction.fetchAnalysis(parseInt(operation.id), parseInt(id), defaultThreshold))
    }

    const formatAnalysis = ana => {
        const qualityDefaultThreshold = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC) || {}

        const samplingMap = keyBy(samplings, 'idSampling')

        const filteredSandreCodes = sandreCodes.filter(sc => sc.field.startsWith('PARAMETRE.'))
        const parametersSandre = groupBy(filteredSandreCodes, sc => sc.field.split('.')[1])

        const datas = ana.filter(a => selection === EMPTY_SELECTION ? true : selections.includes(a.parameter)).map(analyse => {
            const remark = getLabel(remarks, analyse.remark)
            const parameter = parametersIndex[analyse.parameter]
            const parameterValue = parameter ? {
                value: getLabelTruncate(parameter.shortLabel || parameter.name, parameter.code),
                tooltip: parameter.name,
            } : {
                value: `<${analyse.parameter}>`,
            }
            const unit = unitsIndex[analyse.unit]
            const labelUnit = unit?.symbolWithCode

            const fraction = getLabel(fractions, analyse.fraction)
            const fractionTooltip = fraction.length > 20 ? {
                tooltip: fraction,
            } : {}
            const support = getLabel(supports, analyse.support)
            const supportTooltip = support.length > 20 ? {
                tooltip: support,
            } : {}
            // const method = hasValue(analyse.method) ? getLabel(this.props.methods, analyse.method) || `<${analyse.method}>` : ''
            // const methodTooltip = method.length > 30 ? {
            //     tooltip: method,
            // } : {}
            const icon = getQualificationIcon(analyse.qualification)
            const result = calculateThresholdsResult(analyse, qualityDefaultThreshold.thresholds)

            const producer = hasValue(operation.producer) && getObjectLabel(contributorsIndex[operation.producer], 'mnemonique')
            const laboratory = hasValue(analyse.labo) && getObjectLabel(contributorsIndex[analyse.labo], 'mnemonique')
            const sampler = hasValue(operation.sampler) && getObjectLabel(contributorsIndex[operation.sampler], 'mnemonique')

            const foundSampling = samplingMap[analyse.numSampling]
            const sampling = foundSampling ? `${foundSampling.ref || ''} ${foundSampling.refLabo ? `[${foundSampling.refLabo}]` : ''}` : ''

            const parameterSandre = parametersSandre[analyse.parameter]?.find(ps => ps.code === analyse.result)
            const displayResult = !isNil(parameterSandre) ? `${parameterSandre.name} [${analyse.result}]` : result.value

            const analysisDate = analyse.localization === '1' ? getDate(analyse.sampleDate) : getDate(analyse.analysisDate)

            return {
                ...analyse,
                ...result,
                nullValue: { value: statusIcon(analyse, 15, true) },
                qualification: analyse.qualification,
                quantificationLimit: {
                    value: hasValue(analyse.lq) ? analyse.lq : '',
                    align: ALIGN.RIGHT,
                },
                detectionLimit: {
                    value: hasValue(analyse.ld) ? analyse.ld : '',
                    align: ALIGN.RIGHT,
                },
                remarkCode: { value: remark },
                supportCode: analyse.support,
                support: {
                    value: getLabelTruncate(support, analyse.support, 20),
                    ...supportTooltip,
                },
                fractionCode: analyse.fraction,
                fraction: {
                    value: getLabelTruncate(fraction, analyse.fraction, 20),
                    ...fractionTooltip,
                },
                parameter: parameterValue,
                paramCode: analyse.parameter,
                parameterName: getObjectLabel(parameter),
                producer: { value: producer || '' },
                laboratory: { value: laboratory || '' },
                sampler: { value: sampler || '' },
                value: analyse.value,
                resultValue: analyse.result,
                result: {
                    color: result.color,
                    value: analyse.qualification !== '1' ? (
                        <div>
                            <i className='material-icons tiny right no-margin'>{icon}</i>
                            <span className='right' style={{ marginRight: '5px' }}>{displayResult}</span>
                        </div>
                    ) : displayResult,
                    tooltip: (
                        <TooltipAnalyse
                            title={parameter?.shortLabelWithCode || ''}
                            analyse={{ ...analyse, ...result }}
                            threshold={result.threshold}
                        />
                    ),
                    sortValue: analyse.result,
                    align: ALIGN.RIGHT,
                },
                unit: { value: labelUnit || analyse.unit },
                unitCode: analyse.unit,
                analysisDate: { value: analysisDate },
                place: { value: getLocalizationPicto(analyse.localization) },
                // method: { value: substringText(method, 30), color: 'w', ...methodTooltip },
                localization: { value: getLocalizationLabel(analyse.localization) },
                sampling: { value: sampling },
                searchValue: searchAllCharacters(`${analyse.parameter}${parameter?.name}${parameter?.shortLabel}${fraction}${labelUnit}${laboratory ?? ''}${sampling}${unit}${remark}${analyse.result}`),
            }
        }).filter(a => a.searchValue.includes(searchValue))

        return sortBy(datas, 'parameterName')
    }

    const analysisExport = useMemo(() => {
        const formatedAnalysis = propsAnalysis.map(a => {
            return {
                ...a,
                qualification: parseInt(a.qualification),
                status: parseInt(a.status),
                producer: a.producerId,
            }
        })
        const result = formatAnalysis(formatedAnalysis)

        const qualifications = getQualifications()

        const dataExport = result.map(a => {
            return {
                parameterCode: { value: a.paramCode, cellType: 'string' },
                parameter: { value: a.parameter.value },
                result: { value: a.resultValue, format: '0.00', cellType: 'number' },
                value: { value: a.value, color: getMarkerColor(a.color) },
                unit: { value: a.unitCode, cellType: 'right' },
                unitLabel: { value: a.unit.value },
                remarkCode: { value: a.remark, format: '0', cellType: 'number' },
                remark: { value: a.remarkCode.value },
                detectionLimit: { value: a.detectionLimit.value, format: '0.00', cellType: 'number' },
                quantificationLimit: { value: a.quantificationLimit.value, format: '0.00', cellType: 'number' },
                supportCode: { value: a.supportCode, format: '0', cellType: 'number' },
                support: { value: a.support.value },
                fractionCode: { value: a.fractionCode, format: '0', cellType: 'number' },
                fraction: { value: a.fraction.value },
                laboratory: { value: a.laboratory.value },
                sampling: { value: a.sampling.value },
                analysisDate: { value: a.analysisDate.value, format: 'dd/MM/yyyy', cellType: 'date' },
                sampleDate: { value: getDate(a.sampleDate), format: 'dd/MM/yyyy', cellType: 'date' },
                statusCode: { value: a.status, format: '0', cellType: 'number' },
                status: { value: getLabel(getStatuses(), a.status) },
                qualificationCode: { value: a.qualification, format: '0', cellType: 'number' },
                qualification: { value: getLabel(qualifications, a.qualification || UNQUALIFIED) },
                place: { value: a.place?.value?.props?.['data-tooltip'] },
            }
        })
        if (dataExport.length) {
            dataExport[0].headers = exportHeaders
        }
        return dataExport
    }, [propsAnalysis])

    const formatedAnalysis = propsAnalysis.map(a => {
        return {
            ...a,
            qualification: parseInt(a.qualification),
            status: parseInt(a.status),
            producer: a.producerId,
        }
    })
    const result = formatAnalysis(formatedAnalysis)

    const getContentPanel = () => {
        if (!propsAnalysis.length) {
            return (
                <h5 className='padding-top-2 padding-left-2 padding-bottom-2'>{i18n.noAnalysis}</h5>
            )
        }
        return (
            <Stack>
                <Grid2 container spacing={1}>
                    <Grid2 size={4}>
                        <Input
                            onChange={v => setSearchValue(v)}
                            title={i18n.search}
                            value={searchValue}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <ThresholdSelect
                            onChange={onChangeThreshold}
                            selected={threshold}
                            nullLabel='&nbsp;'
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <SelectionSelect
                            onChange={(parameters, value) => {
                                setSelections(parameters)
                                setSelection(value)
                            }}
                        />
                    </Grid2>
                </Grid2>
                <NewTable
                    rows={result}
                    headers={tableHeaders}
                    rowsPerPageOptions={nbPerPageLabel}
                    onClickRow={({ paramCode }) => {
                        setIsOpenChartPopin(true)
                        setSelectedParam(paramCode)
                    }}
                    lineActions={[
                        { icon: 'delete', onClick: ({ id: anaId }) => {
                            setIsOpenConfirmDelete(true)
                            setAnalysis(result.find(a => a.id === anaId))
                        } },
                        { icon: 'edit', onClick: ({ id: anaId }, i) => {
                            setIsOpenAnalysisPopin(true)
                            setAnalysis(result.find(a => a.id === anaId))
                            setIndex(i)
                        } },
                    ]}
                    WrapperComponent={WrapperBox}
                />
            </Stack>
        )
    }

    const getExportModel = () => typeEnvironmentModels.map((model) => {
        const fileNameSplit = model.split('.')
        const name = fileNameSplit.slice(0, -1).join('')
        const ext = fileNameSplit[fileNameSplit.length - 1]
        return {
            name,
            formats: [{
                type: getModelFileType(ext),
                callback: () => exportModelFile({
                    stationId: qualitometer.id.toString(),
                    stationCode: qualitometer.code,
                    stationType: qualitometer.typeName,
                    environmentModels: typeEnvironmentModels,
                    filenameModelExport: model,
                    qualityFilter: {
                        stations: [qualitometer.id],
                        operations: [operation.id],
                    },
                }),
            }],
        }
    })

    const thresholds = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC)?.thresholds || []
    return (
        <div>
            {getContentPanel()}
            <AnalysisPopin
                isOpen={isOpenAnalysisPopin}
                onClose={() => {
                    setAnalysis({})
                    setIsOpenAnalysisPopin(false)
                }}
                onValidate={() => {
                    setAnalysis({})
                    setIsOpenAnalysisPopin(false)
                    reloadOperations()
                }}

                analysisId={analysis.id}
                stationId={parseInt(id)}
                sampleId={sampleId}
                operationId={parseInt(code)}
                onPrevious={() => {
                    const prevIndex = index - 1
                    setIndex(prevIndex)
                    setAnalysis(result[prevIndex])
                    reloadOperations()
                }}
                onPreviousDisabled={index <= 0}
                onNext={() => {
                    const nextIndex = index + 1
                    setIndex(nextIndex)
                    setAnalysis(result[nextIndex])
                    reloadOperations()
                }}
                onNextDisabled={index >= result.length - 1}
            />
            <ConfirmModal
                isOpen={isOpenConfirmDelete}
                title={i18n.sureDeleteAnalysis}
                onValidate={() => {
                    OperationAction.deleteAnalysis(propsAnalysis.find(pA => pA.id === analysis.id))
                        .then(res => {
                            if (res.delete > 0) {
                                dispatch(ToastrAction.success(i18n.elementDeleteSuccess))
                                reloadOperations()
                                setIsOpenConfirmDelete(false)
                                setAnalysis({})
                            } else {
                                dispatch(ToastrAction.error(i18n.deleteError + i18n.analysis))
                            }
                        })
                }}
                onClose={() => {
                    setIsOpenConfirmDelete(false)
                    setAnalysis({})
                }}
            />
            <AnalysisStepper
                isOpen={isOpenAnalysisStepper}
                onClose={() => setIsOpenAnalysisStepper(false) }

                sampleId={sampleId}
            />
            <SuperParameterGraphModal
                isOpen={isOpenChartPopin}
                closeGraph={() => setIsOpenChartPopin(false) }

                parameter={selectedParam}
                qualitometer={parseInt(id)}
                thresholds={thresholds}
                graphOptions={{
                    regroupAxis: false,
                }}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                data={[
                    {
                        name: i18n.resultsTable,
                        formats: [{
                            type: i18n.excelFile,
                            callback: () => dispatch(ExportAction.export(formatData(analysisExport), 'xlsx', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.dateStart)}`)),
                        }, {
                            type: i18n.csvFile,
                            callback: () => dispatch(ExportAction.export(formatData(analysisExport), 'csv', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.dateStart)}`)),
                        }],
                    },
                    ...getExportModel(),
                ]}
            />
        </div>
    )
}

OperationAnalysisPanel.propTypes = {
    id: PropTypes.number.isRequired,
    code: PropTypes.number.isRequired,
    operation: PropTypes.instanceOf(DtoDetailOperation),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoPrettyAnalysisLight)),
    remarks: PropTypes.arrayOf(PropTypes.instanceOf(DtoRemark)),
    threshold: PropTypes.number,
    onChangeThreshold: PropTypes.func,
    uploadFile: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    getOperationFiles: PropTypes.func,
    sampleId: PropTypes.number,
    defaultThreshold: PropTypes.string,
    samplings: PropTypes.arrayOf(DtoSampling),
}

export default OperationAnalysisPanel
